/**
 * DebtorLedgerDrawer — 3-tab side panel (Ledger / Orders / Price List).
 *
 * Extracted from Debtors.jsx (iter200). All data + write handlers come
 * from the parent so this stays a controlled view.
 */
import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, BookOpen, ShoppingCart, Tag, Edit2, Send, RefreshCw, Plus, Check, BarChart3,
} from 'lucide-react';
import LedgerTable from '../ledger/LedgerTable';
import { LedgerFilters, applyLedgerFilters, EMPTY_LEDGER_FILTERS } from '../ledger/LedgerFilters';
import { MonthlyCashflowTable } from '../MonthlyCashflowTable';
import { OpeningRepairBanner } from '../ledger/OpeningRepairBanner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const TABS = [
  { id: 'ledger', label: 'Ledger', icon: BookOpen },
  { id: 'cashflow', label: 'Cash flow', icon: BarChart3 },
  { id: 'orders', label: 'Orders', icon: ShoppingCart },
  { id: 'pricelist', label: 'Price List', icon: Tag },
];

export default function DebtorLedgerDrawer({
  client,
  ledger,
  ledgerLoading,
  ledgerTab,
  setLedgerTab,
  stmtSending,
  onSendStatement,
  onEditProfile,
  onClose,
  onLumpsum,
  onRefreshLedger,    // iter219 — re-fetch ledger after edit/delete
  clientOrders,
  // Price list props
  products,
  priceList,
  newPriceRow,
  setNewPriceRow,
  addPriceRow,
  removePriceRow,
  updatePriceRow,
  priceListSaving,
  onSavePriceList,
  canEditLedger = false,  // iter219 — true for admin/owner/manager/accountant
}) {
  // iter210 — "Both" type clients show a role filter so operators can split
  // the view into As Debtor (sales side) vs As Creditor (purchase side) vs
  // All combined. Other types skip the filter — ledger already segregated.
  // Hooks must run on every render (rules-of-hooks), so they go BEFORE the
  // null guard.
  const isBoth = client?.type === 'Both';
  const [roleFilter, setRoleFilter] = useState('all');
  const [filters, setFilters] = useState(EMPTY_LEDGER_FILTERS);
  const filteredEntries = useMemo(() => {
    const base = (!isBoth || roleFilter === 'all') ? (ledger?.entries || []) : (ledger?.entries || []).filter(e => e.role === roleFilter);
    return applyLedgerFilters(base, filters);
  }, [ledger?.entries, isBoth, roleFilter, filters]);
  const split = ledger?.role_split || { debtor: {}, creditor: {} };

  if (!client) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-start justify-end z-50">
      <div className="h-full w-full max-w-3xl bg-zinc-950 border-l border-zinc-800 flex flex-col" data-testid="debtor-ledger-drawer">
        <div className="px-3 sm:px-5 py-3 sm:py-4 border-b border-zinc-800 flex items-center justify-between gap-2 bg-zinc-900">
          <div className="min-w-0">
            <h3 className="font-heading font-semibold text-zinc-50 truncate">{client.name}</h3>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs px-2 py-0.5 rounded-full border bg-blue-900/40 text-blue-300 border-blue-700">Debtor</span>
              {client.gstin && <span className="text-zinc-500 text-xs font-mono">{client.gstin}</span>}
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
            {client.email && (
              <button data-testid={`send-stmt-btn-${client.id}`} onClick={() => onSendStatement(client)} disabled={stmtSending}
                className="flex items-center gap-1.5 text-xs bg-blue-900/30 hover:bg-blue-800/40 border border-blue-700/50 text-blue-300 px-2.5 sm:px-3 py-1.5 rounded-xl transition-colors disabled:opacity-50">
                {stmtSending ? <RefreshCw size={11} className="animate-spin" /> : <Send size={11} />} <span className="hidden sm:inline">Send Statement</span>
              </button>
            )}
            <button data-testid={`modify-from-ledger-${client.id}`} onClick={() => onEditProfile(client)}
              className="flex items-center gap-1.5 text-xs bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 text-amber-300 px-2.5 sm:px-3 py-1.5 rounded-xl transition-colors">
              <Edit2 size={11} /> <span className="hidden sm:inline">Modify Profile</span>
            </button>
            <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200"><X size={18} /></button>
          </div>
        </div>

        {/* Tab switcher */}
        <div className="flex border-b border-zinc-800 bg-zinc-900 overflow-x-auto">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => setLedgerTab(id)} data-testid={`debtor-drawer-tab-${id}`}
              className={`flex items-center gap-1.5 px-3 sm:px-5 py-3 text-xs font-semibold whitespace-nowrap border-b-2 transition-colors ${ledgerTab === id ? 'border-amber-500 text-amber-400' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
              <Icon size={12} />
              {id === 'orders' ? `Orders (${clientOrders.length})` : label}
              {id === 'pricelist' && priceList.length > 0 && (
                <span className="bg-amber-500/20 text-amber-400 text-[10px] px-1.5 py-0.5 rounded-full ml-1">{priceList.length}</span>
              )}
            </button>
          ))}
        </div>

        {ledger && (
          <>
            {ledger.opening_balance > 0 && (
              <div className="px-4 pt-4" data-testid="opening-balance-card">
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <BookOpen size={14} className="text-amber-400" />
                    <div>
                      <div className="text-amber-300 text-xs font-semibold">Opening Balance (carried forward)</div>
                      <div className="text-zinc-500 text-[10px] mt-0.5">
                        As of {ledger.opening_balance_as_of || '—'}
                        {ledger.client?.opening_balance_note ? ` · ${ledger.client.opening_balance_note}` : ''}
                      </div>
                    </div>
                  </div>
                  <div className={`font-mono font-bold text-sm ${ledger.opening_balance_type === 'Dr' ? 'text-red-400' : 'text-green-400'}`}>
                    {fmt(ledger.opening_balance)} <span className="text-[10px] opacity-70">{ledger.opening_balance_type}</span>
                  </div>
                </div>
              </div>
            )}
            <OpeningRepairBanner ledger={ledger} clientId={client.id} canEdit={canEditLedger} onRepaired={onRefreshLedger} />
            <div className="grid grid-cols-3 gap-2 sm:gap-3 p-3 sm:p-4 border-b border-zinc-800">
              <div className="bg-zinc-900 rounded-xl p-2 sm:p-3 text-center min-w-0">
                <div className="text-zinc-500 text-[10px] sm:text-xs">Total Invoiced</div>
                <div className="text-amber-400 font-mono font-bold text-xs sm:text-sm mt-1 truncate">{fmt(ledger.total_invoiced)}</div>
              </div>
              <div className="bg-zinc-900 rounded-xl p-2 sm:p-3 text-center min-w-0">
                <div className="text-zinc-500 text-[10px] sm:text-xs">Total Received</div>
                <div className="text-green-400 font-mono font-bold text-xs sm:text-sm mt-1 truncate">{fmt(ledger.total_paid)}</div>
              </div>
              <div className="bg-zinc-900 rounded-xl p-2 sm:p-3 text-center min-w-0">
                <div className="text-zinc-500 text-[10px] sm:text-xs">Outstanding ({ledger.balance_type})</div>
                <div className={`font-mono font-bold text-xs sm:text-sm mt-1 truncate ${ledger.outstanding > 0 ? 'text-red-400' : 'text-green-400'}`}>
                  {fmt(Math.abs(ledger.outstanding))}
                </div>
                {/* iter210 — Always show. If outstanding is 0, the operator
                    can still record an upfront advance (booked on-account). */}
                <button
                  data-testid="lumpsum-pay-btn"
                  onClick={onLumpsum}
                  className="mt-2 w-full text-[10px] bg-green-500/10 hover:bg-green-500/20 text-green-400 border border-green-800/50 px-2 py-1 rounded-md transition-colors font-semibold">
                  Receive / Advance
                </button>
              </div>
            </div>
          </>
        )}

        <div className="flex-1 overflow-y-auto">
          {ledgerTab === 'ledger' && (
            <>
              {isBoth && (
                <div className="flex items-center gap-2 px-3 sm:px-4 py-2 border-b border-zinc-800/60 bg-zinc-900/40 sticky top-0 z-10 overflow-x-auto" data-testid="role-filter-bar">
                  <span className="text-[10px] uppercase tracking-wider text-zinc-500 mr-1">View:</span>
                  {[
                    { id: 'all', label: 'All', count: (ledger?.entries || []).length },
                    { id: 'debtor', label: 'As Debtor (Sales)', count: split.debtor?.entries_count || 0 },
                    { id: 'creditor', label: 'As Creditor (Purchases)', count: split.creditor?.entries_count || 0 },
                  ].map(p => (
                    <button key={p.id} data-testid={`role-filter-${p.id}`} onClick={() => setRoleFilter(p.id)}
                      className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${roleFilter === p.id
                        ? 'bg-amber-500/20 border-amber-500/60 text-amber-300'
                        : 'border-zinc-700 text-zinc-400 hover:text-zinc-200'}`}>
                      {p.label} <span className="opacity-60">·{p.count}</span>
                    </button>
                  ))}
                </div>
              )}
              <LedgerFilters clientId={client.id} clientName={client.name} entries={ledger?.entries || []} filters={filters} setFilters={setFilters}
                role={isBoth && roleFilter !== 'all' ? roleFilter : ''} />
              <LedgerTable loading={ledgerLoading} entries={filteredEntries} role={roleFilter === 'creditor' ? 'creditor' : 'debtor'}
                clientId={client.id} clientName={client.name} canEdit={canEditLedger} onRefresh={onRefreshLedger} />
            </>
          )}
          {ledgerTab === 'cashflow' && (
            <MonthlyCashflowTable clientId={client.id} compact side={isBoth && roleFilter === 'creditor' ? 'creditor' : 'debtor'} />
          )}
          {ledgerTab === 'orders' && (
            <OrdersTab orders={clientOrders} />
          )}
          {ledgerTab === 'pricelist' && (
            <PriceListTab
              clientName={client.name}
              priceList={priceList}
              products={products}
              newPriceRow={newPriceRow}
              setNewPriceRow={setNewPriceRow}
              addPriceRow={addPriceRow}
              removePriceRow={removePriceRow}
              updatePriceRow={updatePriceRow}
              saving={priceListSaving}
              onSave={onSavePriceList}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Inner sub-tabs (kept local — they don't need to be reused)
// ─────────────────────────────────────────────────────────────

function OrdersTab({ orders }) {
  if (orders.length === 0) {
    return <div className="text-center text-zinc-500 text-sm py-10">No orders for this client</div>;
  }
  // iter213 — Render effective payment status when a credit-note has been
  // issued against the order. Falls back to the stored payment_status when
  // no credit note exists so legacy rows behave exactly as before.
  const statusOf = (o) => o.effective_payment_status || o.payment_status || 'Unpaid';
  const statusColor = (s) => s === 'Paid' || s === 'Settled'
    ? 'bg-green-900/30 text-green-400 border-green-700'
    : s === 'Partial' || s === 'Partially Paid'
    ? 'bg-amber-900/30 text-amber-400 border-amber-700'
    : 'bg-red-900/20 text-red-400 border-red-800';
  return (
    <div className="p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-x-auto">
        <table className="w-full text-xs min-w-[640px]" data-testid="debtor-orders-table">
          <thead>
            <tr className="border-b border-zinc-800">
              {['Invoice', 'Type', 'Items', 'Total', 'Credit Note', 'Status', 'Payment', 'Date'].map(h => (
                <th key={h} className="text-left text-zinc-500 text-[10px] font-medium px-3 py-2.5">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {orders.map(o => {
              const cn = parseFloat(o.credit_note_amount || 0);
              const status = statusOf(o);
              return (
                <tr key={o.id} className="border-b border-zinc-800/40 hover:bg-zinc-800/20" data-testid={`debtor-order-row-${o.id}`}>
                  <td className="px-3 py-2 font-mono text-amber-400 text-[10px] font-semibold">{o.invoice_number || o.voucher_no || '—'}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full border ${o.gst_enabled ? 'bg-green-900/30 text-green-400 border-green-700' : 'bg-zinc-800 text-zinc-400 border-zinc-700'}`}>
                      {o.gst_enabled ? 'GST' : 'Non-GST'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-zinc-300">{o.items?.length > 1 ? `${o.items.length} items` : (o.product_name || '—')}</td>
                  <td className="px-3 py-2 font-mono text-amber-400">{fmt(o.total_amount)}</td>
                  <td className="px-3 py-2 font-mono text-right">
                    {cn > 0
                      ? <span className="text-teal-400" data-testid={`debtor-order-cn-${o.id}`}>− {fmt(cn)}</span>
                      : <span className="text-zinc-700">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full border ${o.status === 'Dispatched' ? 'bg-green-900/30 text-green-400 border-green-700' : 'bg-zinc-800 text-zinc-400 border-zinc-700'}`}>{o.status}</span>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full border ${statusColor(status)}`} data-testid={`debtor-order-pay-${o.id}`}>{status}</span>
                  </td>
                  <td className="px-3 py-2 text-zinc-500 font-mono">{o.created_at?.slice(0, 10)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PriceListTab({ clientName, priceList, products, newPriceRow, setNewPriceRow, addPriceRow, removePriceRow, updatePriceRow, saving, onSave }) {
  return (
    <div className="p-4 space-y-4" data-testid="price-list-panel">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-zinc-300 text-sm font-semibold">Custom Price List</p>
          <p className="text-zinc-500 text-xs mt-0.5">Set per-product rates for <span className="text-amber-400">{clientName}</span>. These auto-fill in sales vouchers.</p>
        </div>
        <button onClick={onSave} disabled={saving} data-testid="save-price-list-btn"
          className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs px-3 py-1.5 rounded-xl disabled:opacity-50 transition-colors">
          {saving ? <RefreshCw size={11} className="animate-spin" /> : <Check size={11} />} Save
        </button>
      </div>

      {priceList.length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-x-auto">
          <table className="w-full text-xs min-w-[480px]">
            <thead>
              <tr className="border-b border-zinc-800 bg-zinc-800/50">
                {['Product', 'Unit', 'Rate (₹)', 'Discount %', 'Markup %', 'Notes', ''].map(h => (
                  <th key={h} className="text-left text-zinc-500 font-medium px-3 py-2">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {priceList.map((row, idx) => (
                <tr key={row.product_id || `${row.product_name}-${idx}`} className="border-b border-zinc-800/50 hover:bg-zinc-800/20" data-testid={`price-row-${idx}`}>
                  <td className="px-3 py-2 text-zinc-200 font-medium">
                    {row.product_name}
                    {row.item_type === 'raw' && <span className="ml-2 text-[9px] uppercase tracking-wider text-emerald-400 bg-emerald-500/10 border border-emerald-700/30 px-1.5 py-0.5 rounded">RM</span>}
                    {row.item_type === 'finished' && <span className="ml-2 text-[9px] uppercase tracking-wider text-blue-400 bg-blue-500/10 border border-blue-700/30 px-1.5 py-0.5 rounded">FG</span>}
                  </td>
                  <td className="px-3 py-2">
                    <select value={row.unit} onChange={e => updatePriceRow(idx, 'unit', e.target.value)}
                      className="bg-transparent text-zinc-400 focus:outline-none text-xs">
                      {['KG', 'G', 'MT', 'PCS', 'BOX', 'PKT'].map(u => <option key={u} value={u} className="bg-zinc-900">{u}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <input type="number" min="0" step="any" value={row.rate}
                      onChange={e => updatePriceRow(idx, 'rate', parseFloat(e.target.value) || 0)}
                      className="w-20 text-right bg-transparent text-amber-400 font-mono font-semibold focus:outline-none border-b border-transparent focus:border-amber-500" />
                  </td>
                  <td className="px-3 py-2">
                    <input type="number" min="0" max="100" step="any" value={row.discount_pct}
                      onChange={e => updatePriceRow(idx, 'discount_pct', parseFloat(e.target.value) || 0)}
                      className="w-16 text-right bg-transparent text-zinc-400 focus:outline-none border-b border-transparent focus:border-blue-500" />
                  </td>
                  <td className="px-3 py-2">
                    <input type="number" min="0" max="100" step="any" value={row.markup_pct}
                      onChange={e => updatePriceRow(idx, 'markup_pct', parseFloat(e.target.value) || 0)}
                      className="w-16 text-right bg-transparent text-zinc-400 focus:outline-none border-b border-transparent focus:border-green-500" />
                  </td>
                  <td className="px-3 py-2">
                    <input value={row.notes || ''} onChange={e => updatePriceRow(idx, 'notes', e.target.value)}
                      placeholder="—" className="w-24 bg-transparent text-zinc-500 text-xs focus:outline-none border-b border-transparent focus:border-zinc-600" />
                  </td>
                  <td className="px-3 py-2">
                    <button onClick={() => removePriceRow(idx)} className="text-zinc-700 hover:text-red-400 transition-colors"><X size={12} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-zinc-900 border border-dashed border-zinc-700 rounded-xl p-4">
        <p className="text-zinc-500 text-xs mb-3 uppercase tracking-wider font-semibold">Add Product</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-zinc-600 text-[10px] mb-1 block">Product (Finished Good or Raw Material)</label>
            <select value={newPriceRow.product_id}
              onChange={e => {
                const p = products.find(pr => pr.id === e.target.value);
                setNewPriceRow(f => ({
                  ...f, product_id: e.target.value,
                  product_name: p?.name || '',
                  item_type: p?.item_type || 'finished',
                  rate: p?.price_per_unit ? String(p.price_per_unit) : f.rate,
                  unit: (p?.unit || 'KG').toUpperCase(),
                }));
              }}
              data-testid="price-product-select"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-xs focus:outline-none focus:border-amber-500">
              <option value="" className="bg-zinc-900">— Select product —</option>
              <optgroup label="Finished Goods">
                {products.filter(p => p.item_type === 'finished' && !priceList.find(r => r.product_id === p.id)).map(p => (
                  <option key={p.id} value={p.id} className="bg-zinc-900">{p.name}</option>
                ))}
              </optgroup>
              <optgroup label="Raw Materials">
                {products.filter(p => p.item_type === 'raw' && !priceList.find(r => r.product_id === p.id)).map(p => (
                  <option key={p.id} value={p.id} className="bg-zinc-900">{p.name}</option>
                ))}
              </optgroup>
            </select>
          </div>
          <div>
            <label className="text-zinc-600 text-[10px] mb-1 block">Custom Rate (₹)</label>
            <input type="number" min="0" step="any" value={newPriceRow.rate}
              onChange={e => setNewPriceRow(f => ({ ...f, rate: e.target.value }))}
              placeholder="e.g. 245.00" data-testid="price-rate-input"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-amber-400 font-mono text-xs focus:outline-none focus:border-amber-500" />
          </div>
          <div>
            <label className="text-zinc-600 text-[10px] mb-1 block">Unit</label>
            <select value={newPriceRow.unit} onChange={e => setNewPriceRow(f => ({ ...f, unit: e.target.value }))}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-xs focus:outline-none focus:border-amber-500">
              {['KG', 'G', 'MT', 'PCS', 'BOX', 'PKT'].map(u => <option key={u} value={u} className="bg-zinc-900">{u}</option>)}
            </select>
          </div>
          <div>
            <label className="text-zinc-600 text-[10px] mb-1 block">Discount %</label>
            <input type="number" min="0" max="100" step="any" value={newPriceRow.discount_pct}
              onChange={e => setNewPriceRow(f => ({ ...f, discount_pct: e.target.value }))}
              placeholder="0" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-200 text-xs focus:outline-none focus:border-blue-500" />
          </div>
          <div>
            <label className="text-zinc-600 text-[10px] mb-1 block">Markup %</label>
            <input type="number" min="0" max="100" step="any" value={newPriceRow.markup_pct}
              onChange={e => setNewPriceRow(f => ({ ...f, markup_pct: e.target.value }))}
              placeholder="0" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-200 text-xs focus:outline-none focus:border-green-500" />
          </div>
        </div>
        <div className="mt-3">
          <label className="text-zinc-600 text-[10px] mb-1 block">Notes (optional)</label>
          <input value={newPriceRow.notes} onChange={e => setNewPriceRow(f => ({ ...f, notes: e.target.value }))}
            placeholder="e.g. Special rate Q1 2025"
            className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-400 text-xs focus:outline-none focus:border-zinc-600" />
        </div>
        <button type="button" onClick={addPriceRow} data-testid="add-price-row-btn"
          disabled={!newPriceRow.product_id}
          className="mt-3 w-full py-2 rounded-xl bg-zinc-800 hover:bg-zinc-700 border border-dashed border-zinc-600 text-zinc-300 text-xs font-semibold transition-colors flex items-center justify-center gap-1.5 disabled:opacity-40">
          <Plus size={12} /> Add to Price List
        </button>
      </div>
    </div>
  );
}
